import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Sparkles, Send, X, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Assistante WÔMA flottante — invisible jusqu'à clic.
 * Utilise /api/ai/support-chat en backend. Aucun jargon technique côté UI.
 */
export default function SmartAssistant({ user, orderId = null, label = "WÔMA" }) {
  const [open, setOpen] = useState(false);
  const makeMsg = (role, content, extra = {}) => ({
    id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${role}-${Date.now()}-${Math.random()}`,
    role,
    content,
    ...extra,
  });
  const [messages, setMessages] = useState([
    makeMsg(
      "assistant",
      "Bonjour 👋 Je suis WÔMA, votre assistante. Posez-moi vos questions sur votre livraison, je vous réponds en temps réel.",
    ),
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    if (!input.trim() || sending) return;
    if (!user?.id) {
      toast.error("Connectez-vous pour discuter avec WÔMA");
      return;
    }
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, makeMsg("user", text)]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/ai/support-chat`, {
        user_id: user.id,
        message: text,
        order_id: orderId,
        session_id: sessionId,
      });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, makeMsg("assistant", r.data.answer)]);
    } catch (err) {
      if (err.response?.status === 402) {
        const d = err.response.data?.detail || {};
        setMessages((m) => [
          ...m,
          makeMsg(
            "assistant",
            d.message || "Solde insuffisant. Rechargez via Wave ou Orange Money pour continuer.",
            { isError: true },
          ),
        ]);
      } else {
        setMessages((m) => [
          ...m,
          makeMsg("assistant", "Désolé, je suis indisponible un instant. Réessayez ?", { isError: true }),
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  if (!user?.id) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 left-4 z-40 bg-gradient-to-br from-[#003594] to-[#D4AF37] text-white px-4 py-3 rounded-full shadow-2xl hover:scale-105 transition-transform flex items-center gap-2"
          data-testid="smart-assistant-bubble"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-semibold hidden sm:block">{label}</span>
          <MessageCircle className="w-5 h-5 sm:hidden" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 left-4 z-40 w-[92vw] sm:w-[380px] max-w-[95vw] bg-[#0F1B33] rounded-2xl shadow-2xl border border-[#D4AF37]/30 flex flex-col overflow-hidden"
          style={{ maxHeight: "70vh", height: "500px" }}
          data-testid="smart-assistant-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#003594] to-[#D4AF37] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Sparkles className="w-5 h-5 text-white" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              </div>
              <div>
                <p className="font-bold text-white leading-tight">{label}</p>
                <p className="text-[10px] text-white/80">En ligne · réponse instantanée</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="smart-assistant-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-[#003594] text-white rounded-br-sm"
                      : m.isError
                      ? "bg-yellow-900/40 text-yellow-200 border border-yellow-500/30 rounded-bl-sm"
                      : "bg-white/10 text-white rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/10 text-white px-3 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  WÔMA réfléchit…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Écrire un message…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]"
              disabled={sending}
              data-testid="smart-assistant-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-gradient-to-br from-[#003594] to-[#D4AF37] text-white p-2 rounded-full shadow hover:opacity-90 disabled:opacity-50"
              data-testid="smart-assistant-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[9px] text-white/30 text-center pb-1">
            5 FCFA par question · déduits de vos crédits
          </p>
        </div>
      )}
    </>
  );
}
